import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Eye, KeyRound, ListChecks, Lock, Search, Sparkles, UserPlus, Users } from 'lucide-react';
import { useEffect, useId, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { usePlan } from '../lib/billing';
import { formatDate, formatDateTime, humanizeKey } from '../lib/format';
import {
  CATEGORY_LABEL,
  CONFLICT_OPTIONS,
  DEPARTURE_LABEL,
  exampleAddresses,
  NAMING_VARIABLES,
  PLAN_LABEL,
  PLAN_TONE,
  ROLE_CATEGORY_LABEL,
  SAMPLE_SIBLING,
  SAMPLE_STUDENT,
  SAMPLE_TEACHER,
  STATUS_LABEL,
  STATUS_TONE,
  templateProblem,
  useProvisioningAccess,
  useProvisioningOptions,
  type AccountCategory,
  type ConflictStrategy,
  type GoogleAccount,
  type GoogleAccountPage,
  type GoogleAccountStatus,
  type GoogleProvisioningSettings,
  type ProvisioningChange,
  type ProvisioningPreview,
  type RoleCategory,
} from '../lib/google-provisioning';
import { useDebouncedValue } from '../lib/hooks';
import type { BlackbaudIntegration, GoogleIntegration, GoogleProvisioningIntegration } from '../lib/types';
import { CopyField, DataSourcePicker, useIntegrationMutations } from './integration-shell';
import { AddOnRequired } from './plan';
import { useToast } from './toast';
import { Alert, Badge, Button, cn, EmptyState, Field, Input, Modal, PageLoader, Pagination, Select, Spinner, Switch, Table, Td, Textarea, Th } from './ui';

const ORG_UNITS_LIST = 'google-provisioning-org-units';
const DOMAINS_LIST = 'google-provisioning-domains';

function Section({ title, description, children }: { title: string; description?: ReactNode; children: ReactNode }) {
  return (
    <section className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        {description && <p className="mt-0.5 text-sm text-slate-500">{description}</p>}
      </div>
      {children}
    </section>
  );
}

/** Settings tabs are for administrators; everyone else sees them, but can't change them. */
function useAdminForm() {
  const { can } = useAuth();
  return can('ADMIN');
}

function ReadOnlyNote() {
  return <Alert tone="blue">Only administrators can change how provisioning works.</Alert>;
}

/** The pages that act on accounts need an administrator's go-ahead for technicians. */
function AccessGate({ children }: { children: ReactNode }) {
  const access = useProvisioningAccess();
  const plan = usePlan();
  if (!plan.has('GOOGLE_PROVISIONING')) {
    return <AddOnRequired feature="GOOGLE_PROVISIONING" description="Provisioning creates, updates, and retires Google Workspace accounts from Blackbaud." />;
  }
  if (access.isLoading) return <PageLoader />;
  if (!access.data?.canUse) {
    return (
      <EmptyState
        icon={<Lock />}
        title="You don’t have access to provisioning"
        description="An administrator can give technicians access under Departures & access."
      />
    );
  }
  return children;
}

// ---------------------------------------------------------------------------
// Connection
// ---------------------------------------------------------------------------

export function ProvisioningConnection({ integration, all }: { integration: GoogleProvisioningIntegration; all: { blackbaud?: BlackbaudIntegration; google?: GoogleIntegration } }) {
  const isAdmin = useAdminForm();
  const toast = useToast();
  const { update, refresh } = useIntegrationMutations('GOOGLE_PROVISIONING');
  const g = integration.googleProvisioning;
  const googleKey = all.google?.google.schoolServiceAccount ?? null;
  const [source, setSource] = useState<'platform' | 'school'>(g.schoolServiceAccount || !g.platformServiceAccount ? 'school' : 'platform');
  const [keyJson, setKeyJson] = useState('');
  const [adminEmail, setAdminEmail] = useState(integration.settings.adminEmail ?? '');
  const [customerId, setCustomerId] = useState(integration.settings.customerId ?? 'my_customer');
  useEffect(() => setAdminEmail(integration.settings.adminEmail ?? ''), [integration.settings.adminEmail]);

  const saveKey = useMutation({
    mutationFn: () => api.put('/integrations/google-provisioning/service-account', { serviceAccountJson: source === 'school' ? keyJson || null : null }),
    onSuccess: () => {
      refresh();
      setKeyJson('');
      toast.success('Service account saved');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const useGoogleKey = useMutation({
    mutationFn: () => api.post('/integrations/google-provisioning/use-google-key'),
    onSuccess: () => {
      refresh();
      setSource('school');
      toast.success('Using the Google Workspace integration’s service account');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const account = source === 'school' ? g.schoolServiceAccount : g.platformServiceAccount;
  const blackbaud = all.blackbaud;
  const blackbaudReady = !!blackbaud && blackbaud.mode === 'LIVE' && blackbaud.blackbaud.connected;

  return (
    <div className="space-y-8">
      <Section title="Data source">
        <DataSourcePicker integration={integration} />
        {integration.mode === 'DEMO' ? (
          <Alert tone="blue" title="Demo mode doesn’t reach Google">
            It uses the demo Blackbaud roster and a pretend Google Workspace, so you can try naming, org units, and departures safely.
          </Alert>
        ) : blackbaudReady ? (
          <Alert tone="green" title="People come from Blackbaud">
            Connected to {blackbaud!.blackbaud.environmentName ?? 'Blackbaud'}. Choose which roles get accounts under Roles & naming.
          </Alert>
        ) : (
          <Alert tone="amber" title="Connect Blackbaud with live data first">
            Accounts are only made for real people, so provisioning needs the{' '}
            <Link to="/integrations/blackbaud" className="font-medium underline">
              Blackbaud SKY API integration
            </Link>{' '}
            connected with live data.
          </Alert>
        )}
      </Section>

      {integration.mode === 'LIVE' && (
        <>
          <Section title="1. Service account" description="Edvora creates, moves, and suspends users with a Google Cloud service account using domain-wide delegation.">
            <div className="space-y-2">
              <label className={cn('flex items-start gap-3 rounded-lg border px-4 py-3', !g.platformServiceAccount && 'opacity-60')}>
                <input type="radio" className="mt-1" checked={source === 'platform'} disabled={!isAdmin || !g.platformServiceAccount} onChange={() => setSource('platform')} />
                <span>
                  <span className="block text-sm font-medium text-slate-900">Edvora’s service account</span>
                  <span className="block text-xs text-slate-500">{g.platformServiceAccount ? 'Nothing to upload - just authorize its client ID below.' : 'Not set up on this Edvora environment.'}</span>
                </span>
              </label>
              <label className="flex items-start gap-3 rounded-lg border px-4 py-3">
                <input type="radio" className="mt-1" checked={source === 'school'} disabled={!isAdmin} onChange={() => setSource('school')} />
                <span className="flex-1">
                  <span className="block text-sm font-medium text-slate-900">Our own service account</span>
                  <span className="block text-xs text-slate-500">
                    {g.schoolServiceAccount ? `Using ${g.schoolServiceAccount.clientEmail}` : 'Upload a JSON key from your Google Cloud project.'}
                  </span>
                </span>
              </label>
            </div>
            {source === 'school' && isAdmin && (
              <div className="space-y-3 rounded-lg border border-slate-200 p-4">
                {googleKey && googleKey.clientEmail !== g.schoolServiceAccount?.clientEmail && (
                  <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-slate-50 px-3 py-2 text-sm">
                    <span>
                      The Google Workspace integration uses <span className="font-mono text-xs">{googleKey.clientEmail}</span>.
                    </span>
                    <Button size="sm" loading={useGoogleKey.isPending} onClick={() => useGoogleKey.mutate()}>
                      Use the same key
                    </Button>
                  </div>
                )}
                <input
                  type="file"
                  accept=".json,application/json"
                  className="text-sm"
                  onChange={async (event) => {
                    const file = event.target.files?.[0];
                    if (file) setKeyJson(await file.text());
                  }}
                />
                <Textarea
                  rows={4}
                  value={keyJson}
                  onChange={(event) => setKeyJson(event.target.value)}
                  placeholder={g.schoolServiceAccount ? 'A key is saved. Paste or upload a new one to replace it.' : '{ "type": "service_account", … }'}
                  className="font-mono text-xs"
                />
              </div>
            )}
            {isAdmin && (
              <Button loading={saveKey.isPending} disabled={source === 'school' && !keyJson && !g.schoolServiceAccount} onClick={() => saveKey.mutate()}>
                {source === 'school' ? 'Save service account key' : 'Use Edvora’s service account'}
              </Button>
            )}
          </Section>

          <Section
            title="2. Authorize in Google Admin"
            description={
              <>
                In the Google Admin console go to <strong>Security → Access and data control → API controls → Manage domain-wide delegation</strong>. If
                this client ID is already listed for Google Workspace, edit it and add these scopes to the ones it has.
              </>
            }
          >
            {account?.clientId ? <CopyField label="Client ID" value={account.clientId} /> : <Alert tone="amber">Save a service account first to see its client ID.</Alert>}
            <CopyField label="OAuth scopes" value={g.scopes.join(',')} />
          </Section>

          <Section title="3. Admin to act as" description="A Google Workspace admin who can create, move, and suspend users.">
            <div className="max-w-xl space-y-4">
              <Field label="Admin email">
                <Input type="email" value={adminEmail} onChange={(event) => setAdminEmail(event.target.value)} disabled={!isAdmin} placeholder="it-admin@yourschool.org" />
              </Field>
              <Field label="Customer ID" hint='Leave as "my_customer" unless Google support tells you otherwise.'>
                <Input value={customerId} onChange={(event) => setCustomerId(event.target.value)} disabled={!isAdmin} className="font-mono text-xs" />
              </Field>
              {isAdmin && (
                <Button variant="primary" loading={update.isPending} onClick={() => update.mutate({ settings: { adminEmail, customerId } })}>
                  Save
                </Button>
              )}
            </div>
          </Section>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Roles & naming
// ---------------------------------------------------------------------------

function NamingInput({
  label,
  value,
  onChange,
  onFocus,
  disabled,
  example,
  placeholder,
  optional,
}: {
  label: string;
  value: string;
  onChange(value: string): void;
  onFocus(): void;
  disabled: boolean;
  example?: { person: typeof SAMPLE_STUDENT; domain?: string };
  placeholder?: string;
  optional?: boolean;
}) {
  const problem = value.trim() || !optional ? templateProblem(value) : null;
  const address = example && !problem ? exampleAddresses(value, 'PENDING', example.person)[0] : null;
  return (
    <Field
      label={label}
      error={problem ?? undefined}
      hint={
        address ? (
          <>
            {example!.person.firstName} {example!.person.lastName}
            {example!.person.gradYear ? `, class of ${example!.person.gradYear}` : ''} →{' '}
            <span className="font-mono text-slate-700">
              {address}@{example!.domain || 'domain'}
            </span>
          </>
        ) : undefined
      }
    >
      <Input value={value} onChange={(event) => onChange(event.target.value)} onFocus={onFocus} disabled={disabled} placeholder={placeholder} className="font-mono text-sm" />
    </Field>
  );
}

export function ProvisioningNaming({ integration }: { integration: GoogleProvisioningIntegration }) {
  const isAdmin = useAdminForm();
  const options = useProvisioningOptions(isAdmin);
  const { update } = useIntegrationMutations('GOOGLE_PROVISIONING');
  const s = integration.settings;
  const [roles, setRoles] = useState<Record<string, RoleCategory | ''>>(() => Object.fromEntries(s.roles.map((role) => [role.roleId, role.category])));
  const [domains, setDomains] = useState({ studentDomain: s.studentDomain ?? '', facultyDomain: s.facultyDomain ?? '', staffDomain: s.staffDomain ?? '' });
  const [naming, setNaming] = useState({ studentNaming: s.studentNaming, facultyNaming: s.facultyNaming, staffNaming: s.staffNaming });
  const [gradeNaming, setGradeNaming] = useState<Record<string, string>>(s.gradeNaming);
  const [perGrade, setPerGrade] = useState(Object.keys(s.gradeNaming).length > 0);
  const [usePreferredName, setUsePreferredName] = useState(s.usePreferredName);
  const [conflictStrategy, setConflictStrategy] = useState<ConflictStrategy>(s.conflictStrategy);
  // Where a variable goes when its chip is clicked.
  const [focused, setFocused] = useState<{ set(value: string): void; value: string } | null>(null);

  const savedNames = new Map(s.roles.map((role) => [role.roleId, role.roleName]));
  const roleList = [
    ...(options.data?.roles ?? []).map((role) => ({ id: role.id, name: role.name, suggested: role.suggested })),
    ...s.roles.filter((role) => !options.data?.roles.some((known) => known.id === role.roleId)).map((role) => ({ id: role.roleId, name: role.roleName, suggested: null })),
  ];
  const grades = [...new Set([...(options.data?.grades ?? []), ...Object.keys(gradeNaming)])];
  const suggestions = roleList.filter((role) => role.suggested && !roles[role.id]);

  const save = () =>
    update.mutate({
      settings: {
        roles: roleList.flatMap((role) => (roles[role.id] ? [{ roleId: role.id, roleName: role.name || savedNames.get(role.id) || role.id, category: roles[role.id] }] : [])),
        ...domains,
        ...naming,
        gradeNaming: perGrade ? gradeNaming : {},
        usePreferredName,
        conflictStrategy,
      },
    });

  const namingField = (key: keyof typeof naming, label: string, example: { person: typeof SAMPLE_STUDENT; domain?: string }) => (
    <NamingInput
      label={label}
      value={naming[key]}
      disabled={!isAdmin}
      example={example}
      onChange={(value) => setNaming({ ...naming, [key]: value })}
      onFocus={() => setFocused({ value: naming[key], set: (value) => setNaming((current) => ({ ...current, [key]: value })) })}
    />
  );

  return (
    <div className="max-w-3xl space-y-8">
      {!isAdmin && <ReadOnlyNote />}

      <Section title="Who gets an account" description="Choose what each Blackbaud role means. Student, faculty, and staff roles get accounts; the others say where people who left go.">
        {options.data?.rolesError && <Alert tone="amber">Blackbaud roles couldn’t be loaded: {options.data.rolesError}</Alert>}
        {options.isLoading && isAdmin ? (
          <Spinner />
        ) : roleList.length === 0 ? (
          <p className="text-sm text-slate-500">No Blackbaud roles to show yet.</p>
        ) : (
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <Table>
              <thead>
                <tr>
                  <Th>Blackbaud role</Th>
                  <Th>What it means</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {roleList.map((role) => (
                  <tr key={role.id}>
                    <Td className="font-medium text-slate-900">{role.name}</Td>
                    <Td>
                      <Select
                        value={roles[role.id] ?? ''}
                        disabled={!isAdmin}
                        aria-label={`${role.name} role`}
                        onChange={(event) => setRoles({ ...roles, [role.id]: event.target.value as RoleCategory | '' })}
                      >
                        <option value="">Not used</option>
                        {(Object.keys(ROLE_CATEGORY_LABEL) as RoleCategory[]).map((category) => (
                          <option key={category} value={category}>
                            {ROLE_CATEGORY_LABEL[category]}
                          </option>
                        ))}
                      </Select>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
        {isAdmin && suggestions.length > 0 && (
          <Button size="sm" icon={<Sparkles />} onClick={() => setRoles({ ...roles, ...Object.fromEntries(suggestions.map((role) => [role.id, role.suggested!])) })}>
            Fill in {suggestions.length} suggested role{suggestions.length === 1 ? '' : 's'}
          </Button>
        )}
      </Section>

      <Section title="Email domains" description="Where each kind of account’s address is. Students often have their own, like students.yourschool.org.">
        <datalist id={DOMAINS_LIST}>
          {options.data?.domains.map((domain) => (
            <option key={domain} value={domain} />
          ))}
        </datalist>
        <div className="grid gap-4 sm:grid-cols-3">
          {(
            [
              ['studentDomain', 'Students'],
              ['facultyDomain', 'Faculty'],
              ['staffDomain', 'Staff'],
            ] as const
          ).map(([key, label]) => (
            <Field key={key} label={label}>
              <Input list={DOMAINS_LIST} value={domains[key]} disabled={!isAdmin} placeholder="yourschool.org" onChange={(event) => setDomains({ ...domains, [key]: event.target.value })} />
            </Field>
          ))}
        </div>
      </Section>

      <Section
        title="Naming conventions"
        description="How new addresses are made, before the @. Existing accounts keep their addresses. Click a variable to add it where you’re typing."
      >
        <div className="flex flex-wrap gap-1.5">
          {NAMING_VARIABLES.map((variable) => (
            <button
              key={variable.token}
              type="button"
              disabled={!isAdmin || !focused}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                if (!focused) return;
                const next = `${focused.value}${variable.token}`;
                focused.set(next);
                setFocused({ ...focused, value: next });
              }}
              title={variable.label}
              className="rounded-full border border-slate-200 bg-white px-2.5 py-1 font-mono text-xs text-slate-700 hover:border-brand-300 hover:bg-brand-50 disabled:opacity-50"
            >
              {variable.token}
            </button>
          ))}
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          {namingField('studentNaming', perGrade ? 'Students (other grades)' : 'Students', { person: SAMPLE_STUDENT, domain: domains.studentDomain })}
          {namingField('facultyNaming', 'Faculty', { person: { ...SAMPLE_TEACHER, gradYear: 0 }, domain: domains.facultyDomain })}
          {namingField('staffNaming', 'Staff', { person: { ...SAMPLE_TEACHER, gradYear: 0 }, domain: domains.staffDomain })}
        </div>
        <Switch label="A different convention for some grades" description="For example, first names for younger students." checked={perGrade} disabled={!isAdmin} onChange={setPerGrade} />
        {perGrade && (
          <div className="grid gap-3 rounded-lg border border-slate-200 p-4 sm:grid-cols-2">
            {grades.length === 0 && <p className="text-sm text-slate-500">No grades found in Blackbaud yet.</p>}
            {grades.map((grade) => (
              <NamingInput
                key={grade}
                label={grade}
                optional
                value={gradeNaming[grade] ?? ''}
                disabled={!isAdmin}
                placeholder="Same as other grades"
                example={gradeNaming[grade]?.trim() ? { person: SAMPLE_STUDENT, domain: domains.studentDomain } : undefined}
                onChange={(value) => setGradeNaming({ ...gradeNaming, [grade]: value })}
                onFocus={() => setFocused({ value: gradeNaming[grade] ?? '', set: (value) => setGradeNaming((current) => ({ ...current, [grade]: value })) })}
              />
            ))}
          </div>
        )}
        <Switch
          label="Use preferred names"
          description="Name accounts with the preferred first name in Blackbaud, when someone has one."
          checked={usePreferredName}
          disabled={!isAdmin}
          onChange={setUsePreferredName}
        />
      </Section>

      <Section
        title="When an address is taken"
        description={`Say ${SAMPLE_STUDENT.firstName} ${SAMPLE_STUDENT.lastName} already has one, and her brother ${SAMPLE_SIBLING.firstName} just enrolled.`}
      >
        <div className="grid gap-2 sm:grid-cols-2">
          {CONFLICT_OPTIONS.map((option) => {
            const tried = exampleAddresses(naming.studentNaming, option.value, SAMPLE_SIBLING, 3);
            return (
              <label
                key={option.value}
                className={cn(
                  'flex cursor-pointer gap-3 rounded-lg border px-4 py-3',
                  conflictStrategy === option.value ? 'border-brand-500 bg-brand-50/60 ring-1 ring-brand-500' : 'border-slate-200 hover:border-slate-300',
                )}
              >
                <input type="radio" name="conflict" className="mt-1" checked={conflictStrategy === option.value} disabled={!isAdmin} onChange={() => setConflictStrategy(option.value)} />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-slate-900">{option.title}</span>
                  <span className="block text-xs text-slate-500">{option.text}</span>
                  {tried.length > 0 && (
                    <span className="mt-1 block truncate font-mono text-xs text-slate-600">
                      {tried.length > 1 ? `${tried.slice(1).join(', ')}…` : `${tried[0]} is taken → Pending users`}
                    </span>
                  )}
                </span>
              </label>
            );
          })}
        </div>
      </Section>

      {isAdmin && (
        <Button variant="primary" loading={update.isPending} onClick={save}>
          Save roles & naming
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Org units
// ---------------------------------------------------------------------------

export function ProvisioningOrgUnits({ integration }: { integration: GoogleProvisioningIntegration }) {
  const isAdmin = useAdminForm();
  const options = useProvisioningOptions(isAdmin);
  const { update } = useIntegrationMutations('GOOGLE_PROVISIONING');
  const s = integration.settings;
  const [units, setUnits] = useState({
    studentOrgUnit: s.studentOrgUnit ?? '',
    facultyOrgUnit: s.facultyOrgUnit ?? '',
    staffOrgUnit: s.staffOrgUnit ?? '',
    alumniOrgUnit: s.alumniOrgUnit ?? '',
    pastStudentOrgUnit: s.pastStudentOrgUnit ?? '',
    pastStaffOrgUnit: s.pastStaffOrgUnit ?? '',
  });
  const [classes, setClasses] = useState<Record<string, string>>(s.classOrgUnits);
  const years = [...new Set([...(options.data?.classes ?? []).map(String), ...Object.keys(classes)])].sort();
  const fromTemplate = (year: string) => (units.studentOrgUnit.includes('{gradYear}') ? units.studentOrgUnit.replaceAll('{gradYear}', year) : units.studentOrgUnit);

  const field = (key: keyof typeof units, label: string, hint?: string) => (
    <Field label={label} hint={hint}>
      <Input list={ORG_UNITS_LIST} value={units[key]} disabled={!isAdmin} placeholder="/" onChange={(event) => setUnits({ ...units, [key]: event.target.value })} />
    </Field>
  );

  return (
    <div className="max-w-3xl space-y-8">
      {!isAdmin && <ReadOnlyNote />}
      <datalist id={ORG_UNITS_LIST}>
        {options.data?.orgUnits.map((unit) => (
          <option key={unit} value={unit} />
        ))}
      </datalist>
      {options.data?.orgUnitsError && <Alert tone="amber">Org units couldn’t be loaded from Google, so type their paths: {options.data.orgUnitsError}</Alert>}
      <p className="text-sm text-slate-500">
        Accounts Edvora creates go here, and move when someone changes class or leaves. Leave one blank to leave those accounts where they are.
      </p>

      <Section title="Students" description="Each class can have its own org unit. Classes left blank use the student org unit.">
        {field('studentOrgUnit', 'Student org unit', 'Use {gradYear} for the class year, e.g. /Students/Class of {gradYear}')}
        {years.length > 0 && (
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <Table>
              <thead>
                <tr>
                  <Th>Class</Th>
                  <Th>Org unit</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {years.map((year) => (
                  <tr key={year}>
                    <Td className="font-medium whitespace-nowrap text-slate-900">Class of {year}</Td>
                    <Td>
                      <Input
                        list={ORG_UNITS_LIST}
                        value={classes[year] ?? ''}
                        disabled={!isAdmin}
                        aria-label={`Class of ${year} org unit`}
                        placeholder={fromTemplate(year) || 'Where it is now'}
                        onChange={(event) => setClasses({ ...classes, [year]: event.target.value })}
                      />
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Section>

      <Section title="Faculty and staff">
        <div className="grid gap-4 sm:grid-cols-2">
          {field('facultyOrgUnit', 'Faculty')}
          {field('staffOrgUnit', 'Staff')}
        </div>
      </Section>

      <Section title="People who left" description="Where accounts go when their owner leaves, by the Blackbaud role they have now.">
        <div className="grid gap-4 sm:grid-cols-3">
          {field('alumniOrgUnit', 'Alumni')}
          {field('pastStudentOrgUnit', 'Past students')}
          {field('pastStaffOrgUnit', 'Past faculty and staff')}
        </div>
      </Section>

      {isAdmin && (
        <Button variant="primary" loading={update.isPending} onClick={() => update.mutate({ settings: { ...units, classOrgUnits: classes } })}>
          Save org units
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Departures & access
// ---------------------------------------------------------------------------

export function ProvisioningPolicies({ integration }: { integration: GoogleProvisioningIntegration }) {
  const isAdmin = useAdminForm();
  const options = useProvisioningOptions(isAdmin);
  const { update } = useIntegrationMutations('GOOGLE_PROVISIONING');
  const s = integration.settings;
  const [form, setForm] = useState({
    autoApply: s.autoApply,
    suspendDeparted: s.suspendDeparted,
    suspendAfterDays: String(s.suspendAfterDays),
    manageExistingAccounts: s.manageExistingAccounts,
    changePasswordAtNextLogin: s.changePasswordAtNextLogin,
    maxNewAccounts: String(s.maxNewAccounts),
    departureSafetyPercent: String(s.departureSafetyPercent),
  });
  const [technicianIds, setTechnicianIds] = useState(s.technicianIds);

  return (
    <div className="max-w-2xl space-y-8">
      {!isAdmin && <ReadOnlyNote />}
      <Section
        title="Carrying changes out"
        description="A sync works out what Google Workspace should look like. This decides whether it does it, or asks first."
      >
        <Switch
          label="Make the changes automatically"
          description={
            form.autoApply
              ? 'Each sync creates, matches, moves, suspends and drops accounts as it finds work to do.'
              : 'Each sync works everything out and changes nothing. Every change waits under Pending approval until someone approves it, one at a time.'
          }
          checked={form.autoApply}
          disabled={!isAdmin}
          onChange={(autoApply) => setForm({ ...form, autoApply })}
        />
      </Section>

      <Section title="When someone leaves" description="Their account moves to the org unit for people who left right away.">
        <Switch
          label="Suspend the account"
          description="Suspended accounts can’t sign in, but keep their email and files."
          checked={form.suspendDeparted}
          disabled={!isAdmin}
          onChange={(suspendDeparted) => setForm({ ...form, suspendDeparted })}
        />
        {form.suspendDeparted && (
          <Field label="Days to wait before suspending" hint="0 suspends it as soon as the next sync sees they left.">
            <Input type="number" min={0} max={365} className="w-32" value={form.suspendAfterDays} disabled={!isAdmin} onChange={(event) => setForm({ ...form, suspendAfterDays: event.target.value })} />
          </Field>
        )}
        <Switch
          label="Also manage accounts that already existed"
          description="Accounts Edvora matched to people (rather than created) move between org units and are suspended too."
          checked={form.manageExistingAccounts}
          disabled={!isAdmin}
          onChange={(manageExistingAccounts) => setForm({ ...form, manageExistingAccounts })}
        />
      </Section>

      <Section title="New accounts">
        <Switch
          label="Ask for a new password at first sign-in"
          description="Edvora sets a random first password, which people with access can look up for 30 days."
          checked={form.changePasswordAtNextLogin}
          disabled={!isAdmin}
          onChange={(changePasswordAtNextLogin) => setForm({ ...form, changePasswordAtNextLogin })}
        />
      </Section>

      <Section title="Safety limits" description="A sync that would go past these makes those changes wait, so a mistake in Blackbaud or the settings can’t flood Google Workspace.">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Most new accounts in one sync">
            <Input type="number" min={1} max={5000} value={form.maxNewAccounts} disabled={!isAdmin} onChange={(event) => setForm({ ...form, maxNewAccounts: event.target.value })} />
          </Field>
          <Field label="Most accounts departing at once (%)" hint="Raise it for one sync at graduation, if needed.">
            <Input type="number" min={1} max={100} value={form.departureSafetyPercent} disabled={!isAdmin} onChange={(event) => setForm({ ...form, departureSafetyPercent: event.target.value })} />
          </Field>
        </div>
      </Section>

      <Section title="Technicians who can use provisioning" description="They can see accounts, choose addresses for pending users, look up first passwords, preview, and sync. Administrators always can.">
        {!isAdmin ? (
          <p className="text-sm text-slate-500">{technicianIds.length === 0 ? 'No technicians.' : `${technicianIds.length} technician${technicianIds.length === 1 ? '' : 's'}.`}</p>
        ) : options.isLoading ? (
          <Spinner />
        ) : options.data?.technicians.length ? (
          <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
            {options.data.technicians.map((technician) => (
              <label key={technician.id} className="flex cursor-pointer items-center gap-3 px-4 py-2.5">
                <input
                  type="checkbox"
                  className="size-4 rounded border-slate-300 accent-brand-700"
                  checked={technicianIds.includes(technician.id)}
                  onChange={(event) => setTechnicianIds(event.target.checked ? [...technicianIds, technician.id] : technicianIds.filter((id) => id !== technician.id))}
                />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-slate-900">{technician.name}</span>
                  <span className="block truncate text-xs text-slate-500">{technician.email}</span>
                </span>
              </label>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500">The school has no technicians yet.</p>
        )}
      </Section>

      {isAdmin && (
        <Button
          variant="primary"
          loading={update.isPending}
          onClick={() =>
            update.mutate({
              settings: {
                ...form,
                suspendAfterDays: Number(form.suspendAfterDays) || 0,
                maxNewAccounts: Number(form.maxNewAccounts) || 50,
                departureSafetyPercent: Number(form.departureSafetyPercent) || 25,
                technicianIds,
              },
            })
          }
        >
          Save
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pending users
// ---------------------------------------------------------------------------

function useAccountsRefresh() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: ['google-provisioning'] });
}

function PendingRow({ account, domain }: { account: GoogleAccount; domain: string | undefined }) {
  const toast = useToast();
  const refresh = useAccountsRefresh();
  const [username, setUsername] = useState('');
  const inputId = useId();
  const name = useMutation({
    mutationFn: () => api.post<GoogleAccount>(`/google-provisioning/accounts/${account.id}/name`, { username }),
    onSuccess: (created) => {
      refresh();
      toast.success(`Created ${created.email}`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const skip = useMutation({
    mutationFn: (value: boolean) => api.post(`/google-provisioning/accounts/${account.id}/skip`, { skip: value }),
    onSuccess: () => refresh(),
    onError: (err) => toast.error(errorMessage(err)),
  });
  const suggested = account.suggestedEmail?.split('@')[0];

  return (
    <li className="flex flex-col gap-3 px-5 py-4 lg:flex-row lg:items-center">
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-2 font-medium text-slate-900">
          {account.personId ? (
            <Link to={`/people/${account.personId}`} className="hover:text-brand-700">
              {account.firstName} {account.lastName}
            </Link>
          ) : (
            `${account.firstName} ${account.lastName}`
          )}
          <Badge>{CATEGORY_LABEL[account.category]}</Badge>
          {account.gradYear && <span className="text-xs font-normal text-slate-500">Class of {account.gradYear}</span>}
        </p>
        {account.pendingReason && <p className="mt-0.5 text-sm text-amber-800">{account.pendingReason}</p>}
      </div>
      {account.status === 'SKIPPED' ? (
        <Button size="sm" loading={skip.isPending} onClick={() => skip.mutate(false)}>
          Put back in line
        </Button>
      ) : (
        <form
          className="flex w-full flex-wrap items-center gap-2 lg:w-auto"
          onSubmit={(event) => {
            event.preventDefault();
            if (username.trim()) name.mutate();
          }}
        >
          <label htmlFor={inputId} className="sr-only">
            Address for {account.firstName} {account.lastName}
          </label>
          <div className="flex min-w-0 flex-1 items-center rounded-lg border border-slate-300 bg-white focus-within:border-brand-500 focus-within:ring-1 focus-within:ring-brand-500 lg:flex-none">
            <input
              id={inputId}
              value={username}
              onChange={(event) => setUsername(event.target.value.trim())}
              placeholder={suggested ? `e.g. ${suggested}2` : 'username'}
              className="w-full min-w-0 rounded-l-lg bg-transparent px-3 py-1.5 font-mono text-sm outline-none lg:w-40"
            />
            <span className="max-w-[55%] shrink-0 truncate border-l border-slate-200 bg-slate-50 px-2.5 py-1.5 font-mono text-xs text-slate-500" title={`@${domain ?? ''}`}>
              @{domain ?? '…'}
            </span>
          </div>
          <Button size="sm" type="submit" variant="primary" icon={<UserPlus />} loading={name.isPending} disabled={!username.trim() || !domain}>
            Create
          </Button>
          <Button size="sm" variant="ghost" loading={skip.isPending} onClick={() => skip.mutate(true)}>
            Skip
          </Button>
        </form>
      )}
    </li>
  );
}

function ChangeRow({ change, domain }: { change: ProvisioningChange; domain: string | undefined }) {
  const toast = useToast();
  const refresh = useAccountsRefresh();
  const [username, setUsername] = useState('');
  const inputId = useId();
  const approve = useMutation({
    mutationFn: () => api.post<{ summary: string }>(`/google-provisioning/changes/${change.id}/approve`, change.needsEmail ? { username } : {}),
    onSuccess: (result) => {
      refresh();
      toast.success(result.summary);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const dismiss = useMutation({
    mutationFn: () => api.post(`/google-provisioning/changes/${change.id}/dismiss`),
    onSuccess: () => {
      refresh();
      toast.success(`Put aside for ${change.name}`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <li className="flex flex-col gap-3 px-5 py-4 lg:flex-row lg:items-center">
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-2 font-medium text-slate-900">
          {change.name}
          <Badge>{CATEGORY_LABEL[change.category]}</Badge>
          <Badge tone="blue">{change.label}</Badge>
        </p>
        <p className="mt-0.5 text-sm text-slate-600">
          {[change.email, change.orgUnitPath && `to ${change.orgUnitPath}`, change.detail].filter(Boolean).join(' · ') || 'Nothing else to say'}
        </p>
        {change.lastError && <p className="mt-0.5 text-sm text-red-800">{change.lastError}</p>}
      </div>
      <form
        className="flex w-full flex-wrap items-center gap-2 lg:w-auto"
        onSubmit={(event) => {
          event.preventDefault();
          if (!change.needsEmail || username.trim()) approve.mutate();
        }}
      >
        {change.needsEmail && (
          <>
            <label htmlFor={inputId} className="sr-only">
              Address for {change.name}
            </label>
            <div className="flex min-w-0 flex-1 items-center rounded-lg border border-slate-300 bg-white focus-within:border-brand-500 focus-within:ring-1 focus-within:ring-brand-500 lg:flex-none">
              <input
                id={inputId}
                value={username}
                onChange={(event) => setUsername(event.target.value.trim())}
                placeholder="username"
                className="w-full min-w-0 rounded-l-lg bg-transparent px-3 py-1.5 font-mono text-sm outline-none lg:w-40"
              />
              <span className="max-w-[55%] shrink-0 truncate border-l border-slate-200 bg-slate-50 px-2.5 py-1.5 font-mono text-xs text-slate-500" title={`@${domain ?? ''}`}>
                @{domain ?? '…'}
              </span>
            </div>
          </>
        )}
        <Button size="sm" type="submit" variant="primary" icon={<Check />} loading={approve.isPending} disabled={change.needsEmail && (!username.trim() || !domain)}>
          Approve
        </Button>
        <Button size="sm" variant="ghost" loading={dismiss.isPending} onClick={() => dismiss.mutate()}>
          Put aside
        </Button>
      </form>
    </li>
  );
}

export function ProvisioningPending({ integration }: { integration: GoogleProvisioningIntegration }) {
  const [showSkipped, setShowSkipped] = useState(false);
  const status: GoogleAccountStatus = showSkipped ? 'SKIPPED' : 'PENDING';
  const s = integration.settings;
  const domainFor = (category: AccountCategory) => ({ STUDENT: s.studentDomain, FACULTY: s.facultyDomain, STAFF: s.staffDomain })[category];
  const list = useQuery({
    queryKey: ['google-provisioning', 'accounts', status],
    queryFn: () => api.get<GoogleAccountPage>('/google-provisioning/accounts', { status, pageSize: 200 }),
  });
  const changes = useQuery({
    queryKey: ['google-provisioning', 'changes'],
    queryFn: () => api.get<{ items: ProvisioningChange[]; total: number }>('/google-provisioning/changes'),
  });
  const waiting = changes.data?.items ?? [];

  return (
    <AccessGate>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="max-w-2xl text-sm text-slate-500">
            People whose address the naming convention couldn’t make, usually because someone already has it. Choose the part before the @, and the account is
            created right away.
          </p>
          <Button size="sm" variant="ghost" onClick={() => setShowSkipped(!showSkipped)}>
            {showSkipped ? 'Show pending users' : 'Show skipped people'}
          </Button>
        </div>
        {!integration.enabled && !showSkipped && <Alert tone="amber">Turn provisioning on to create accounts.</Alert>}
        {!showSkipped && !s.autoApply && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-slate-900">
              Waiting for approval{waiting.length > 0 && <span className="ml-1 font-normal text-slate-500">({waiting.length})</span>}
            </h3>
            <p className="max-w-2xl text-sm text-slate-500">
              Automatic provisioning is off, so each sync works out what Google Workspace should look like and changes nothing. Approve a change and it happens -
              the account is created, matched, moved, suspended, or dropped.
            </p>
            <div className="overflow-hidden rounded-lg border border-slate-200">
              {changes.isLoading ? (
                <PageLoader />
              ) : waiting.length === 0 ? (
                <EmptyState icon={<ListChecks />} title="Nothing waiting" description="The last sync found nothing to change." />
              ) : (
                <ul className="divide-y divide-slate-100">
                  {waiting.map((change) => (
                    <ChangeRow key={change.id} change={change} domain={domainFor(change.category)} />
                  ))}
                </ul>
              )}
            </div>
            <h3 className="pt-2 text-sm font-semibold text-slate-900">Waiting for an address</h3>
          </div>
        )}
        <div className="overflow-hidden rounded-lg border border-slate-200">
          {list.isLoading ? (
            <PageLoader />
          ) : !list.data?.items.length ? (
            <EmptyState
              icon={<ListChecks />}
              title={showSkipped ? 'Nobody is skipped' : 'Nobody is waiting for an address'}
              description={showSkipped ? undefined : 'After a sync, anyone whose address is taken shows up here.'}
            />
          ) : (
            <ul className="divide-y divide-slate-100">
              {list.data.items.map((account) => (
                <PendingRow key={account.id} account={account} domain={domainFor(account.category)} />
              ))}
            </ul>
          )}
        </div>
      </div>
    </AccessGate>
  );
}

// ---------------------------------------------------------------------------
// Accounts
// ---------------------------------------------------------------------------

function PasswordModal({ account, onClose }: { account: GoogleAccount; onClose(): void }) {
  const toast = useToast();
  const reveal = useQuery({
    queryKey: ['google-provisioning', 'password', account.id],
    queryFn: () => api.post<{ password: string; expiresAt: string }>(`/google-provisioning/accounts/${account.id}/password`),
    gcTime: 0,
    staleTime: Infinity,
  });
  return (
    <Modal open onClose={onClose} size="sm" title="First password" description={account.email ?? undefined} footer={<Button onClick={onClose}>Done</Button>}>
      {reveal.isLoading ? (
        <Spinner />
      ) : reveal.error ? (
        <Alert tone="red">{errorMessage(reveal.error)}</Alert>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Input readOnly value={reveal.data!.password} className="font-mono" onFocus={(event) => event.target.select()} />
            <Button
              onClick={async () => {
                await navigator.clipboard.writeText(reveal.data!.password);
                toast.success('Copied');
              }}
            >
              Copy
            </Button>
          </div>
          <p className="text-xs text-slate-500">
            Available until {formatDate(reveal.data!.expiresAt)}. Looking it up is recorded in the activity log.
            {' '}They’ll be asked for a new password when they first sign in, if that’s turned on.
          </p>
        </div>
      )}
    </Modal>
  );
}

function PreviewModal({ onClose }: { onClose(): void }) {
  const preview = useQuery({
    queryKey: ['google-provisioning', 'preview'],
    queryFn: () => api.post<ProvisioningPreview>('/google-provisioning/preview'),
    gcTime: 0,
    staleTime: Infinity,
    retry: false,
  });
  const warnings = preview.data?.log.filter((line) => line.level !== 'info') ?? [];
  const changes = preview.data?.plan.filter((item) => item.action !== 'LINK') ?? [];
  const matched = (preview.data?.plan.length ?? 0) - changes.length;
  const matchedText = `${matched} existing account${matched === 1 ? '' : 's'}`;
  return (
    <Modal open onClose={onClose} size="2xl" title="Preview changes" description="What a sync would do right now. Nothing changes until one runs." footer={<Button onClick={onClose}>Close</Button>}>
      {preview.isLoading ? (
        <div className="flex h-40 flex-col items-center justify-center gap-2 text-sm text-slate-500">
          <Spinner />
          Reading Blackbaud and Google Workspace…
        </div>
      ) : preview.error ? (
        <Alert tone="red">{errorMessage(preview.error)}</Alert>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-6">
            {Object.entries(preview.data!.stats)
              .filter(([key, value]) => value > 0 || key === 'created')
              .map(([key, value]) => (
                <div key={key} className="rounded-lg border border-slate-200 px-3 py-2">
                  <p className="text-lg font-semibold">{value.toLocaleString()}</p>
                  <p className="text-xs text-slate-500">{humanizeKey(key)}</p>
                </div>
              ))}
          </div>
          {warnings.map((line, index) => (
            <Alert key={index} tone={line.level === 'error' ? 'red' : 'amber'}>
              {line.message}
            </Alert>
          ))}
          {changes.length === 0 ? (
            <EmptyState icon={<ListChecks />} title="Nothing to change" description={matched ? `${matchedText} would be matched to ${matched === 1 ? 'its owner' : 'their owners'}.` : undefined} />
          ) : (
            <div className="max-h-[50vh] overflow-auto rounded-lg border border-slate-200">
              <Table>
                <thead>
                  <tr>
                    <Th>Change</Th>
                    <Th>Person</Th>
                    <Th>Account</Th>
                    <Th>Org unit</Th>
                    <Th>Details</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {changes.map((item, index) => (
                    <tr key={`${item.blackbaudUserId}-${index}`}>
                      <Td>
                        <Badge tone={PLAN_TONE[item.action]}>{PLAN_LABEL[item.action]}</Badge>
                      </Td>
                      <Td className="whitespace-nowrap">
                        {item.name}
                        <span className="block text-xs text-slate-500">{CATEGORY_LABEL[item.category]}</span>
                      </Td>
                      <Td className="font-mono text-xs">{item.email ?? '—'}</Td>
                      <Td className="text-xs text-slate-600">{item.orgUnitPath ?? '—'}</Td>
                      <Td className="text-xs text-slate-600">{item.detail ?? ''}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
          {changes.length > 0 && matched > 0 && <p className="text-xs text-slate-500">Plus {matchedText} matched to {matched === 1 ? 'its owner' : 'their owners'}.</p>}
        </div>
      )}
    </Modal>
  );
}

const PAGE_SIZE = 50;

export function ProvisioningAccounts() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<GoogleAccountStatus | ''>('');
  const [category, setCategory] = useState<AccountCategory | ''>('');
  const [page, setPage] = useState(1);
  const [previewing, setPreviewing] = useState(false);
  const [password, setPassword] = useState<GoogleAccount | null>(null);
  const query = useDebouncedValue(search.trim(), 300);
  useEffect(() => setPage(1), [query, status, category]);

  const summary = useQuery({
    queryKey: ['google-provisioning', 'summary'],
    queryFn: () => api.get<{ byStatus: Partial<Record<GoogleAccountStatus, number>>; suspendingThisWeek: number }>('/google-provisioning/summary'),
  });
  const list = useQuery({
    queryKey: ['google-provisioning', 'accounts', 'all', query, status, category, page],
    queryFn: () => api.get<GoogleAccountPage>('/google-provisioning/accounts', { search: query, status, category, page, pageSize: PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });

  return (
    <AccessGate>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          {(Object.keys(STATUS_LABEL) as GoogleAccountStatus[]).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setStatus(status === value ? '' : value)}
              className={cn(
                'rounded-full border px-3 py-1 text-xs font-medium',
                status === value ? 'border-brand-500 bg-brand-50 text-brand-800' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300',
              )}
            >
              {STATUS_LABEL[value]} <span className="text-slate-400">{summary.data?.byStatus[value] ?? 0}</span>
            </button>
          ))}
          {!!summary.data?.suspendingThisWeek && <span className="text-xs text-violet-700">{summary.data.suspendingThisWeek} suspended within a week</span>}
          <Button className="ml-auto" icon={<Eye />} onClick={() => setPreviewing(true)}>
            Preview changes
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-full sm:w-72">
            <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search name, address, or Blackbaud ID" className="pl-9" aria-label="Search accounts" />
          </div>
          <Select value={category} onChange={(event) => setCategory(event.target.value as AccountCategory | '')} className="w-auto" aria-label="Kind of account">
            <option value="">Everyone</option>
            {(Object.keys(CATEGORY_LABEL) as AccountCategory[]).map((value) => (
              <option key={value} value={value}>
                {CATEGORY_LABEL[value]}
              </option>
            ))}
          </Select>
          {list.isFetching && !list.isLoading && <Spinner className="size-4" />}
        </div>

        <div className="overflow-hidden rounded-lg border border-slate-200">
          {list.isLoading ? (
            <PageLoader />
          ) : !list.data?.items.length ? (
            <EmptyState icon={<Users />} title="No accounts yet" description="Run a sync, or preview what one would do." />
          ) : (
            <>
              <Table>
                <thead>
                  <tr>
                    <Th>Person</Th>
                    <Th>Account</Th>
                    <Th>Org unit</Th>
                    <Th>Status</Th>
                    <Th className="text-right">Actions</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {list.data.items.map((account) => (
                    <tr key={account.id}>
                      <Td className="whitespace-nowrap">
                        {account.personId ? (
                          <Link to={`/people/${account.personId}`} className="font-medium text-slate-900 hover:text-brand-700">
                            {account.firstName} {account.lastName}
                          </Link>
                        ) : (
                          <span className="font-medium text-slate-900">
                            {account.firstName} {account.lastName}
                          </span>
                        )}
                        <span className="block text-xs text-slate-500">
                          {CATEGORY_LABEL[account.category]}
                          {account.gradYear ? ` · Class of ${account.gradYear}` : ''}
                        </span>
                      </Td>
                      <Td>
                        <span className="font-mono text-xs text-slate-800">{account.email ?? account.suggestedEmail ?? '—'}</span>
                        <span className="block text-xs text-slate-500">
                          {account.origin === 'CREATED'
                            ? `Created by Edvora${account.namedByName ? ` · named by ${account.namedByName}` : ''}`
                            : account.origin === 'LINKED'
                              ? 'Existing account, matched'
                              : account.status === 'PENDING'
                                ? 'Wanted address'
                                : ''}
                        </span>
                      </Td>
                      <Td className="text-xs text-slate-600">{account.orgUnitPath ?? '—'}</Td>
                      <Td className="whitespace-nowrap">
                        <Badge tone={STATUS_TONE[account.status]}>{STATUS_LABEL[account.status]}</Badge>
                        <span className="block text-xs text-slate-500">
                          {account.status === 'DEPARTED' && account.departedAs && DEPARTURE_LABEL[account.departedAs]}
                          {account.status === 'DEPARTED' && account.suspendAt && ` · suspends ${formatDate(account.suspendAt)}`}
                          {account.status === 'SUSPENDED' && account.suspendedAt && `Since ${formatDate(account.suspendedAt)}`}
                        </span>
                        {account.lastError && (
                          <span className="block max-w-xs truncate text-xs text-red-700" title={account.lastError}>
                            {account.lastError}
                          </span>
                        )}
                      </Td>
                      <Td className="text-right whitespace-nowrap">
                        {account.hasPassword && (
                          <Button size="sm" variant="ghost" icon={<KeyRound />} onClick={() => setPassword(account)}>
                            First password
                          </Button>
                        )}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <Pagination page={page} pageSize={PAGE_SIZE} total={list.data.total} onPage={setPage} />
            </>
          )}
        </div>
        <p className="text-xs text-slate-500">Updated {list.data?.items[0] ? formatDateTime(list.data.items[0].updatedAt) : '—'} at the latest. Syncs keep this list current.</p>
      </div>
      {previewing && <PreviewModal onClose={() => setPreviewing(false)} />}
      {password && <PasswordModal account={password} onClose={() => setPassword(null)} />}
    </AccessGate>
  );
}
